/**
 * Choosing an option 选择, as the reading Exercise and the grammar exercise
 * both ask for it (CB-22, CB-23).
 *
 * An option is a row: a radio button kept out of sight, the letter in its
 * circle, and what the option says. `partials/lianxi/yuedu.html`,
 * `partials/lianxi/yufa-xuanze.html` and `src/user/custom-yufa-player.ts` all
 * draw it under the names here, and `modules/lianxi` styles it once, so the two
 * kinds of Exercise look and answer alike. What decides whether an option is
 * right is the Exercise's own. This is the row, the keys and the marks.
 *
 * **A letter or a digit chooses, anywhere on the page.** A reader need not
 * click into the question first, and never chooses by typing into a field.
 * Enter on a button or a link stays that control's own.
 *
 * **A press anywhere on the row chooses its option**, bar a button inside it,
 * such as the reading Exercise's English. The click a label hands on to its
 * radio button, and the one an arrow key gives the radio button it lands on,
 * are left to the browser, so an arrow only moves the choice. An arrow passing
 * over the right option is not an answer.
 */

/** The letters the options are drawn with, in order. */
export const optionLetters: readonly string[] = ["A", "B", "C", "D", "E", "F"];

/**
 * A row, as `modules/lianxi` styles it, or a placement's slot, which is
 * chosen and marked the same way inside its sentence.
 */
export const optionRow = ".lianxi-xuanze-option, .lianxi-xuanze-slot";

/** What a marked row carries: the right option, and a wrong one chosen. */
const rightAttribute = "data-cb-xuanze-right";
const wrongAttribute = "data-cb-xuanze-wrong";

/** The label a marked right option shows, drawn hidden in every row. */
const rightLabel = "[data-cb-xuanze-right-label]";

/**
 * The option a key names, by its letter or its digit, counting from nought.
 * @param key - The key's `key`.
 * @param count - How many options there are.
 * @returns The option's place, or nothing for any other key.
 */
export function optionByKey(key: string, count: number): number | undefined {
  const lower = key.toLowerCase();
  const at = /^[1-9]$/u.test(lower)
    ? Number(lower) - 1
    : optionLetters.findIndex((letter) => letter.toLowerCase() === lower);

  return at >= 0 && at < count ? at : undefined;
}

/**
 * Whether a key belongs to something the reader is typing into: a text field,
 * a textarea, a select or anything `contenteditable`. A radio button is not
 * one, being where a chosen option's focus is.
 */
export function typingInto(target: EventTarget | null): boolean {
  return (
    (target instanceof HTMLInputElement &&
      target.type !== "radio" &&
      target.type !== "checkbox") ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement &&
      (target.isContentEditable ||
        target.closest("wa-input, wa-textarea, wa-select") !== null))
  );
}

/** Whether a key is the browser's, held with a modifier. */
export function modified(event: KeyboardEvent): boolean {
  return event.metaKey || event.ctrlKey || event.altKey;
}

/** Whether Enter on this target is its own control's, a button's or a link's. */
export function ownEnter(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest(
      "a[href], button, wa-button, select, wa-select, summary, textarea, [contenteditable=true], [role=button]",
    ) !== null
  );
}

/**
 * Whether a click on a row is the reader's press, which chooses, rather than
 * one the browser passed on to the radio button or one on a control inside
 * the row.
 */
export function pressedRow(event: Event): boolean {
  const target = event.target;

  return !(
    target instanceof HTMLInputElement ||
    (target instanceof Element && target.closest("button, a[href]") !== null)
  );
}

/** The row a radio button is the option of. */
export function rowOf(radio: Element): HTMLElement | null {
  return radio.closest<HTMLElement>(optionRow);
}

/**
 * Mark a question's rows: the right options ringed, and a wrong one chosen.
 * The rest fade, by the radio buttons being disabled.
 *
 * **The label goes on a right option the reader did not choose** (CB-121).
 * On a right answer the line under the question says so already, and the
 * label over the option the reader has just chosen said it twice. After a
 * miss it is what points to the right option.
 * @param radios - Every option, as drawn.
 * @param right - Whether each option is a right one.
 * @param chosen - The option the reader chose, or nothing where it is not
 *   known, as for a kept miss from before the choice was kept.
 */
export function markOptions(
  radios: readonly HTMLInputElement[],
  right: (radio: HTMLInputElement) => boolean,
  chosen: HTMLInputElement | undefined,
): void {
  for (const radio of radios) {
    const row = rowOf(radio);

    radio.disabled = true;

    if (row === null) {
      continue;
    }

    if (right(radio)) {
      row.setAttribute(rightAttribute, "");
      row
        .querySelector(rightLabel)
        ?.toggleAttribute("hidden", radio === chosen);
    } else if (radio === chosen) {
      row.setAttribute(wrongAttribute, "");
    }
  }
}

/**
 * Put the rows back as drawn: nothing chosen, nothing marked.
 * @param radios - Every option, as drawn.
 */
export function clearOptions(radios: readonly HTMLInputElement[]): void {
  for (const radio of radios) {
    const row = rowOf(radio);

    radio.disabled = false;
    radio.checked = false;

    if (row !== null) {
      row.removeAttribute(rightAttribute);
      row.removeAttribute(wrongAttribute);
      row.querySelector(rightLabel)?.setAttribute("hidden", "");
    }
  }
}

/** Whether a row has been marked, either way. */
export function isMarked(row: Element): boolean {
  return row.hasAttribute(rightAttribute) || row.hasAttribute(wrongAttribute);
}
